import React, { useMemo } from 'react';
import TypeaheadSelect from './TypeaheadSelect';
import { ControlRow, SizeTag } from '../styles/CustomerSpecsIndexStyles';

/**
 * Shared size multi-select — dropdown + removable pills — used by Recommended
 * Sizing, Spec Size Range, and Subbing Options on the Customer Specs Entry Form
 * and Manage drawer. The dropdown offers only not-yet-selected sizes; clicking a
 * pill removes it (matching the original Subbing Options pill).
 *
 * `sort` (default true): the sizing fields render AND persist in the commodity's
 * configured Size-Options order (the index in `orderedSizes` — the same
 * `getOrderedSizesForCommodity` output the retired slider used). Subbing Options
 * passes sort={false} to keep click order, exactly as before.
 *
 * Props:
 *   value        string[]   selected sizes
 *   onChange     (string[]) => void
 *   orderedSizes string[]   the commodity's sizes in configured order
 *   disabled     boolean
 *   ariaLabel    string     labels the add dropdown
 *   placeholder  string
 *   sort         boolean    sort selection by commodity order (default true)
 */
export default function SizingMultiSelect({
  value,
  onChange,
  orderedSizes,
  disabled = false,
  ariaLabel,
  placeholder = 'Add size...',
  sort = true,
}) {
  const selected = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  const orderIndex = useMemo(() => {
    const m = new Map();
    (orderedSizes || []).forEach((s, i) => m.set(s, i));
    return m;
  }, [orderedSizes]);

  // Sizing fields display in commodity order (dropping any size no longer in the
  // commodity list); Subbing Options keeps click order (sort=false).
  const pills = useMemo(() => {
    if (!sort) return selected;
    return selected
      .filter((s) => orderIndex.has(s))
      .sort((a, b) => orderIndex.get(a) - orderIndex.get(b));
  }, [selected, orderIndex, sort]);

  const options = useMemo(
    () => (orderedSizes || [])
      .filter((s) => !selected.includes(s))
      .map((s) => ({ value: s, label: s })),
    [orderedSizes, selected]
  );

  const hasSizes = Array.isArray(orderedSizes) && orderedSizes.length > 0;
  const pickerDisabled = disabled || !hasSizes || options.length === 0;
  const pickerPlaceholder = hasSizes && options.length === 0 ? 'All sizes added' : placeholder;

  const addSize = (val) => {
    const v = String(val ?? '').trim();
    if (!v || selected.includes(v)) return;
    const next = [...selected, v];
    onChange(sort ? next.sort((a, b) => (orderIndex.get(a) ?? 0) - (orderIndex.get(b) ?? 0)) : next);
  };

  const removeSize = (s) => onChange(selected.filter((x) => x !== s));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <ControlRow>
        <TypeaheadSelect
          ariaLabel={ariaLabel || placeholder}
          value=""
          placeholder={pickerPlaceholder}
          size="md"
          disabled={pickerDisabled}
          options={options}
          onChange={addSize}
        />
      </ControlRow>
      {pills.length > 0 ? (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {pills.map((s) => (
            <SizeTag
              key={s}
              role="button"
              tabIndex={0}
              aria-label={`Remove ${s}`}
              title={`Remove ${s}`}
              onClick={() => removeSize(s)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); removeSize(s); }
              }}
              style={{ cursor: 'pointer', background: 'var(--grey-600)' }}
            >
              {s}
            </SizeTag>
          ))}
        </div>
      ) : null}
    </div>
  );
}
